import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ActivityCard from '@/components/activity/ActivityCard';
import {
  customerActivityService,
  type CustomerActivityItem,
} from '@/api/services/customerActivity';
import { applyMarkAllRead, applyMarkRead } from '@/utils/customerActivityState';
import { useAutoRefreshPolling } from '@/hooks/useAutoRefreshPolling';
import { subscribeCustomerActivityRefresh, invalidateCustomerActivityRefresh } from '@/utils/customerActivityRefresh';
import { tActivity } from '@/i18n/activity';
import { useMe } from '@/context/MeContext';
import { canViewWorkspaceActivity } from '@/utils/navCapabilities';
import { resolveActivityActionCapabilities } from '@/utils/activityActions';

export default function NotificationCenter() {
  const { me } = useMe();
  const canViewActivity = canViewWorkspaceActivity(me);
  const actionCapabilities = resolveActivityActionCapabilities(me);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<CustomerActivityItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    if (!canViewActivity) return;
    const [data, unread] = await Promise.all([
      customerActivityService.list({ page_size: 15, unread_only: false }),
      customerActivityService.unreadCount(),
    ]);
    const attention = data.items.filter(
      (i) =>
        !i.read &&
        (i.attention || i.category === 'SUBSCRIPTION' || i.category === 'NOTIFICATION'),
    );
    setItems(attention.length > 0 ? attention : data.items.slice(0, 10));
    setUnreadCount(unread.unreadCount);
  }, [canViewActivity]);

  const { autoRefresh } = useAutoRefreshPolling({
    hasData: () => items.length > 0,
    load: async () => {
      await fetchNotifications();
    },
    autoRefresh: {
      intervalMs: 60_000,
      storageKey: 'notification-center-auto-refresh',
      enabled: canViewActivity,
    },
  });

  useEffect(() => {
    return subscribeCustomerActivityRefresh(() => {
      void fetchNotifications();
    });
  }, [fetchNotifications]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function handleMarkRead(id: string) {
    setItems((prev) => applyMarkRead(prev, id));
    setUnreadCount((n) => Math.max(0, n - 1));
  }

  async function markRead(id: string) {
    handleMarkRead(id);
    try {
      await customerActivityService.markRead(id);
      invalidateCustomerActivityRefresh();
      await autoRefresh.refresh();
    } catch {
      await autoRefresh.refresh();
    }
  }

  async function markAllRead() {
    setItems((prev) => applyMarkAllRead(prev));
    setUnreadCount(0);
    try {
      await customerActivityService.markAllRead();
      invalidateCustomerActivityRefresh();
      await autoRefresh.refresh();
    } catch {
      await autoRefresh.refresh();
    }
  }

  if (!canViewActivity) {
    return null;
  }

  return (
    <div className="notification-center" ref={panelRef}>
      <button
        type="button"
        className="btn btn-ghost notification-center__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-expanded={open}
      >
        🔔
        {unreadCount > 0 ? <span className="notification-center__badge">{unreadCount}</span> : null}
      </button>
      {open ? (
        <div className="notification-center__panel" role="dialog" aria-label="Notifications">
          <div className="notification-center__header">
            <strong>Notifications</strong>
            {unreadCount > 0 ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => void markAllRead()}>
                {tActivity('activity.markAllRead')}
              </button>
            ) : null}
          </div>
          <ul className="notification-center__list">
            {items.length === 0 ? (
              <li className="muted notification-center__empty">No notifications yet.</li>
            ) : (
              items.map((n) => (
                <li
                  key={n.id}
                  className={`notification-center__item${n.read ? '' : ' notification-center__item--unread'}`}
                >
                  <ActivityCard
                    item={n}
                    compact
                    onMarkRead={(id) => void markRead(id)}
                    actions={actionCapabilities}
                  />
                </li>
              ))
            )}
          </ul>
          <div className="notification-center__footer">
            <Link to="/customer-activity" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>
              View activity timeline
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
